import React, { useState } from 'react';
import { buildChildrenMap, ChildrenMap } from '@/components/shared/Standards/standardsTreeUtils';
import { StandardNode } from '@/components/shared/Standards/types';
import { CheckboxState } from '../standardsFilterUtils';
import StandardRow from './StandardRow';

interface SearchResultRowProps {
  childrenMap: ChildrenMap;
  getCheckboxState: (id: number) => CheckboxState;
  node: StandardNode;
  onCheck: (id: number, checked: boolean) => void;
}

const SearchResultRow = ({ childrenMap, getCheckboxState, node, onCheck }: SearchResultRowProps) => {
  const [expanded, setExpanded] = useState(true);
  const children = childrenMap.get(node.id) ?? [];

  return (
    <StandardRow
      checkboxState={getCheckboxState(node.id)}
      expandable={children.length > 0}
      expanded={expanded}
      node={node}
      onCheck={onCheck}
      onToggle={() => setExpanded(!expanded)}
    >
      {children.map(child => (
        <SearchResultRow
          key={child.id}
          childrenMap={childrenMap}
          getCheckboxState={getCheckboxState}
          node={child}
          onCheck={onCheck}
        />
      ))}
    </StandardRow>
  );
};

interface StandardsSearchResultsProps {
  getCheckboxState: (id: number) => CheckboxState;
  nodes: StandardNode[];
  onCheck: (id: number, checked: boolean) => void;
}

// The search response (matching subtrees: hits + ancestors + descendants) rendered as a tree that
// starts fully expanded — the closure is complete, so collapsing is purely local state, and the
// parent keys this component by term so a new search resets to expanded.
// An empty result renders nothing; the no-results message is PLAT-17707's.
const StandardsSearchResults = ({ getCheckboxState, nodes, onCheck }: StandardsSearchResultsProps) => {
  const childrenMap = buildChildrenMap(nodes);

  return (
    <>
      {(childrenMap.get(null) ?? []).map(root => (
        <SearchResultRow
          key={root.id}
          childrenMap={childrenMap}
          getCheckboxState={getCheckboxState}
          node={root}
          onCheck={onCheck}
        />
      ))}
    </>
  );
};

export default StandardsSearchResults;
